import { useState } from "react";
import { Button } from "../../ui/button";
import { Label } from "../../ui/label";
import { Textarea } from "../../ui/textarea";
import { Sparkles, Loader2, Download, Lock, History, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import CreditCostTag from "../../CreditCostTag";

export function SignOffTab({
  projectId, token,
  closureData, saving, aiGenerating,
  executiveSummary, setExecutiveSummary,
  closureReason, setClosureReason,
  closureStatus, setClosureStatus,
  closureNotes, setClosureNotes,
  aiDrafts,
  isClosed,
  saveDetails, performSignOff, generateAiDraft, exportPdf,
}) {
  const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

  // Reopen modal
  const [showReopenModal, setShowReopenModal] = useState(false);
  const [reopenReason, setReopenReason] = useState("");
  const [reopening, setReopening] = useState(false);

  const handleReopen = async () => {
    if (!reopenReason.trim()) { toast.error("Reopen reason is required"); return; }
    setReopening(true);
    try {
      const res = await fetch(`${API}/projects/${projectId}/closure/reopen`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ reason: reopenReason }),
      });
      if (!res.ok) { const err = await res.json(); throw new Error(err.detail || "Reopen failed"); }
      toast.success("Project reopened successfully");
      window.location.reload();
    } catch (error) { toast.error(error.message); } finally { setReopening(false); }
  };

  const applyAiSummary = () => {
    if (aiDrafts?.executive_summary) {
      setExecutiveSummary(aiDrafts.executive_summary);
      toast.info("AI draft applied. Review and edit before saving.");
    }
  };

  const statusOptions = [
    { value: "completed", label: "Completed", desc: "Project achieved its goals and is formally closed" },
    { value: "closed_early", label: "Closed Early", desc: "Project stopped before full completion with partial results" },
    { value: "cancelled", label: "Cancelled", desc: "Project discontinued — no results to sustain" },
    { value: "on_hold", label: "On Hold", desc: "Project paused — may resume later" },
  ];

  return (
    <div className="space-y-6">
      {/* Executive Summary */}
      <div className="bg-card border border-border rounded-lg p-6 space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="font-heading font-semibold">Executive Summary</h3>
          <div className="flex gap-2">
            <Button
              variant="outline" size="sm"
              onClick={() => generateAiDraft("executive_summary")}
              disabled={!!aiGenerating}
              className="gap-2"
              data-testid="ai-draft-summary-btn"
            >
              {aiGenerating === "executive_summary" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
              AI Draft
              <CreditCostTag cost={20} />
            </Button>
            {aiDrafts?.executive_summary && (
              <Button variant="outline" size="sm" onClick={applyAiSummary} data-testid="apply-ai-summary-btn">Apply AI Draft</Button>
            )}
          </div>
        </div>
        <Textarea
          placeholder="Write a leadership-ready executive summary of this project's outcomes..."
          value={executiveSummary}
          onChange={(e) => setExecutiveSummary(e.target.value)}
          rows={8}
          disabled={isClosed}
          data-testid="executive-summary-input"
        />
      </div>

      {/* Closure Details */}
      <div className="bg-card border border-border rounded-lg p-6 space-y-4">
        <h3 className="font-heading font-semibold">Closure Details</h3>

        <div className="space-y-2">
          <Label>Closure Status</Label>
          <div className="grid md:grid-cols-2 gap-3">
            {statusOptions.map((opt) => (
              <label
                key={opt.value}
                className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${closureStatus === opt.value ? "border-primary bg-primary/5" : "border-border hover:border-primary/40"}`}
                data-testid={`closure-status-${opt.value}`}
              >
                <input
                  type="radio" name="closure-status"
                  value={opt.value}
                  checked={closureStatus === opt.value}
                  onChange={(e) => setClosureStatus(e.target.value)}
                  className="mt-1 accent-primary"
                  disabled={isClosed}
                />
                <div>
                  <p className="text-sm font-medium">{opt.label}</p>
                  <p className="text-xs text-muted-foreground">{opt.desc}</p>
                </div>
              </label>
            ))}
          </div>
        </div>

        <div className="space-y-2">
          <Label>Closure Reason</Label>
          <Textarea
            placeholder="Describe why the project is being closed at this point..."
            value={closureReason}
            onChange={(e) => setClosureReason(e.target.value)}
            rows={3}
            disabled={isClosed}
            data-testid="closure-reason-input"
          />
        </div>

        <div className="space-y-2">
          <Label>Additional Notes</Label>
          <Textarea
            placeholder="Any additional notes for the closure record..."
            value={closureNotes}
            onChange={(e) => setClosureNotes(e.target.value)}
            rows={3}
            disabled={isClosed}
            data-testid="closure-notes-input"
          />
        </div>

        {!isClosed && (
          <Button onClick={saveDetails} disabled={saving} data-testid="save-closure-details-btn">
            {saving ? "Saving..." : "Save Closure Details"}
          </Button>
        )}
      </div>

      {/* Sign Off / Actions */}
      <div className="bg-card border border-border rounded-lg p-6 space-y-4">
        <h3 className="font-heading font-semibold">Formal Sign-Off</h3>

        {isClosed && closureData?.sign_off ? (
          <div className="space-y-4">
            <div className="p-4 bg-emerald-500/5 border border-emerald-500/20 rounded-lg" data-testid="sign-off-record">
              <div className="flex items-center gap-2 mb-2">
                <Lock className="w-4 h-4 text-emerald-400" />
                <span className="text-sm font-semibold text-emerald-400">Project Closed</span>
              </div>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div><span className="text-muted-foreground">Signed by:</span> <strong>{closureData.sign_off.signed_by_name}</strong></div>
                <div><span className="text-muted-foreground">Role:</span> <strong>{closureData.sign_off.role}</strong></div>
                <div><span className="text-muted-foreground">Status:</span> <strong>{closureData.sign_off.closure_status?.replace("_", " ")}</strong></div>
                <div><span className="text-muted-foreground">Date:</span> <strong>{new Date(closureData.sign_off.signed_at).toLocaleDateString()}</strong></div>
              </div>
              {closureData.sign_off.comments && (
                <p className="text-sm text-muted-foreground mt-2">{closureData.sign_off.comments}</p>
              )}
            </div>

            {/* Reopen */}
            {!showReopenModal ? (
              <Button variant="outline" onClick={() => setShowReopenModal(true)} className="gap-2" data-testid="reopen-project-btn">
                <History className="w-4 h-4" /> Reopen Project
              </Button>
            ) : (
              <div className="p-4 bg-amber-500/5 border border-amber-500/20 rounded-lg space-y-3" data-testid="reopen-modal">
                <div className="flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4 text-amber-400" />
                  <span className="text-sm font-semibold text-amber-400">Reopen Project</span>
                </div>
                <p className="text-xs text-muted-foreground">Reopening will restore the project to editable state. Closure history will be preserved.</p>
                <Textarea
                  placeholder="Reason for reopening this project (required)..."
                  value={reopenReason}
                  onChange={(e) => setReopenReason(e.target.value)}
                  rows={3}
                  data-testid="reopen-reason-input"
                />
                <div className="flex gap-2">
                  <Button onClick={handleReopen} disabled={reopening || !reopenReason.trim()} variant="destructive" data-testid="confirm-reopen-btn">
                    {reopening ? "Reopening..." : "Confirm Reopen"}
                  </Button>
                  <Button variant="ghost" onClick={() => setShowReopenModal(false)}>Cancel</Button>
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="space-y-4">
            <div className="p-4 bg-muted/30 border border-border rounded-lg">
              <p className="text-sm text-muted-foreground">
                By signing off, you formally close this project. The project will become read-only.
                Only project champions, sponsors, or owners can perform this action.
              </p>
            </div>
            <Button
              onClick={() => performSignOff(closureStatus)}
              disabled={saving || !closureStatus}
              className="gap-2"
              data-testid="sign-off-btn"
            >
              <Lock className="w-4 h-4" />
              {saving ? "Closing..." : "Sign Off & Close Project"}
            </Button>
          </div>
        )}
      </div>

      {/* Reopen History */}
      {closureData?.reopen_history?.length > 0 && (
        <div className="bg-card border border-border rounded-lg p-6 space-y-3" data-testid="reopen-history">
          <div className="flex items-center gap-2">
            <History className="w-4 h-4 text-muted-foreground" />
            <h3 className="font-heading font-semibold text-sm">Reopen History</h3>
          </div>
          {closureData.reopen_history.map((r, i) => (
            <div key={i} className="p-3 bg-background border border-border rounded-lg text-xs">
              <div className="flex items-center justify-between">
                <span className="font-medium">{r.reopened_by_name}</span>
                <span className="text-muted-foreground">{new Date(r.reopened_at).toLocaleString()}</span>
              </div>
              <p className="text-muted-foreground mt-1">Reason: {r.reason}</p>
            </div>
          ))}
        </div>
      )}

      {/* PDF Export */}
      <div className="bg-card border border-border rounded-lg p-6 space-y-4">
        <h3 className="font-heading font-semibold">Export Reports</h3>
        <div className="flex gap-3">
          <Button variant="outline" onClick={() => exportPdf("executive")} className="gap-2" data-testid="export-executive-pdf-btn">
            <Download className="w-4 h-4" /> Executive Summary PDF
          </Button>
          <Button variant="outline" onClick={() => exportPdf("full")} className="gap-2" data-testid="export-full-pdf-btn">
            <Download className="w-4 h-4" /> Full Closure Report PDF
          </Button>
        </div>
      </div>
    </div>
  );
}
